import React, { useCallback, useEffect, useState } from 'react';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import {
  AlertCircle, CalendarClock, ChevronDown, ChevronRight, Loader2,
  MessageSquare, Play, Salad, ShieldCheck, Video,
} from 'lucide-react';
import api from '../../lib/api';

const fmtDateTime = (d) => (d ? new Date(d).toLocaleString('en-IN', {
  day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit',
}) : '—');
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-IN', {
  day: 'numeric', month: 'short', year: 'numeric',
}) : '—');
const mmss = (s) => {
  if (!s) return null;
  const m = Math.floor(s / 60);
  return `${m} min`;
};

const Stat = ({ label, value }) => (
  <div className="p-3 rounded-xl border bg-muted/30">
    <p className="text-xs text-muted-foreground">{label}</p>
    <p className="text-lg font-semibold">{value}</p>
  </div>
);

const REC_LABEL = {
  ready: { text: 'Recording ready', cls: 'bg-emerald-600 text-white' },
  encoding: { text: 'Processing', cls: 'bg-amber-500 text-white' },
  uploading: { text: 'Processing', cls: 'bg-amber-500 text-white' },
  downloading: { text: 'Processing', cls: 'bg-amber-500 text-white' },
  pending: { text: 'Awaiting Zoom', cls: 'bg-slate-200 text-slate-700' },
  failed: { text: 'Recording failed', cls: 'bg-rose-600 text-white' },
  skipped_too_large: { text: 'Too large to store', cls: 'bg-rose-600 text-white' },
  deleted: { text: 'Deleted (retention)', cls: 'bg-slate-200 text-slate-600' },
};

const RecordingRow = ({ rec, canPlay, onPlay }) => {
  if (!rec) {
    return (
      <p className="text-[11px] text-muted-foreground" data-testid="recording-none">
        No recording stored for this call.
      </p>
    );
  }
  const meta = REC_LABEL[rec.status] || { text: rec.status, cls: 'bg-slate-200 text-slate-700' };
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid={`recording-${rec.id}`}>
      <Badge className={meta.cls} data-testid={`recording-status-${rec.id}`}>{meta.text}</Badge>
      {rec.duration_seconds ? <span className="text-[11px] text-muted-foreground">{mmss(rec.duration_seconds)}</span> : null}
      {rec.size_mb ? <span className="text-[11px] text-muted-foreground">{rec.size_mb} MB</span> : null}
      {rec.retention_until && rec.status !== 'deleted' && (
        <span className="text-[11px] text-muted-foreground" data-testid={`recording-retention-${rec.id}`}>
          kept till {fmtDate(rec.retention_until)}
        </span>
      )}
      {rec.playable && canPlay && (
        <Button size="sm" variant="outline" className="h-7 px-2"
                onClick={() => onPlay(rec)} data-testid={`recording-play-${rec.id}`}>
          <Play className="h-3 w-3 mr-1" />Watch
        </Button>
      )}
      {rec.status === 'ready' && !canPlay && (
        <span className="text-[11px] text-muted-foreground flex items-center gap-1" data-testid="recording-superadmin-only">
          <ShieldCheck className="h-3 w-3" />Superadmin only
        </span>
      )}
      {rec.error && <span className="text-[11px] text-rose-600">{rec.error}</span>}
    </div>
  );
};

const Messages = ({ consultId }) => {
  const [msgs, setMsgs] = useState([]);
  const [before, setBefore] = useState(undefined);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (cursor) => {
    setLoading(true);
    try {
      const params = cursor != null ? { before: cursor } : {};
      const res = await api.get(`/api/admin/diet-communication/thread/${consultId}/messages`, { params });
      setMsgs((prev) => (cursor != null ? [...prev, ...(res.data?.messages || [])] : (res.data?.messages || [])));
      setBefore(res.data?.next_before);
      setHasMore(!!res.data?.has_more);
    } finally {
      setLoading(false);
    }
  }, [consultId]);

  useEffect(() => { load(); }, [load]);

  if (loading && !msgs.length) {
    return <div className="py-4 flex justify-center"><Loader2 className="h-4 w-4 animate-spin" /></div>;
  }
  return (
    <div className="space-y-2 mt-2" data-testid={`thread-messages-${consultId}`}>
      {msgs.map((m, i) => (
        <div key={m.message_id || i} className="text-xs rounded-lg border p-2 bg-background">
          <div className="flex flex-wrap items-center gap-2 mb-0.5">
            <span className="font-medium">{m.sender_name || m.sender_type}</span>
            <Badge variant="outline" className="text-[10px] px-1 py-0">{m.sender_type}</Badge>
            {m.kind && m.kind !== 'text' && (
              <Badge variant="outline" className="text-[10px] px-1 py-0">{m.kind.replace(/_/g, ' ')}</Badge>
            )}
            <span className="text-[10px] text-muted-foreground ml-auto">{fmtDateTime(m.timestamp)}</span>
          </div>
          <p className="whitespace-pre-wrap text-muted-foreground">{m.content || '—'}</p>
        </div>
      ))}
      {hasMore && (
        <Button size="sm" variant="ghost" disabled={loading} onClick={() => load(before)}
                data-testid={`thread-load-more-${consultId}`}>
          {loading ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}Load older messages
        </Button>
      )}
    </div>
  );
};

const Thread = ({ thread, canPlay, onPlay }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-xl border overflow-hidden" data-testid={`diet-thread-${thread.id}`}>
      <button type="button" onClick={() => setOpen((o) => !o)}
              className="w-full px-3 py-2 bg-muted/40 flex flex-wrap items-center gap-x-3 gap-y-1 text-left hover:bg-muted/60 transition-colors"
              data-testid={`diet-thread-toggle-${thread.id}`}>
        {open ? <ChevronDown className="h-4 w-4 shrink-0" /> : <ChevronRight className="h-4 w-4 shrink-0" />}
        <span className="text-sm font-medium flex items-center gap-1.5">
          <MessageSquare className="h-4 w-4 text-primary" />
          {thread.provider_name || 'Nutritionist'}
        </span>
        {thread.episode ? <span className="text-[11px] text-muted-foreground">episode {thread.episode}</span> : null}
        <Badge variant="outline" className="text-[10px]">{thread.status}</Badge>
        <span className="text-[11px] text-muted-foreground ml-auto">
          {thread.message_count ?? 0} message(s) · last {fmtDateTime(thread.last_message_at)}
        </span>
      </button>

      <div className="p-3 space-y-3">
        {(thread.calls || []).length > 0 && (
          <div className="space-y-2">
            {thread.calls.map((c) => (
              <div key={c.id} className="rounded-lg border p-2 bg-muted/20" data-testid={`diet-call-${c.id}`}>
                <div className="flex flex-wrap items-center gap-2 mb-1">
                  <Video className="h-3.5 w-3.5 text-primary" />
                  <span className="text-xs font-medium">{fmtDateTime(c.scheduled_at)}</span>
                  <span className="text-[11px] text-muted-foreground">{c.duration_minutes} min</span>
                  <Badge variant="outline" className="text-[10px]">{c.status}</Badge>
                </div>
                <RecordingRow rec={c.recording} canPlay={canPlay} onPlay={onPlay} />
              </div>
            ))}
          </div>
        )}

        {(thread.paid || []).length > 0 && (
          <div className="flex flex-wrap gap-2">
            {thread.paid.map((p) => (
              <Badge key={p.id} className="bg-emerald-50 text-emerald-900 border border-emerald-200"
                     data-testid={`diet-paid-${p.id}`}>
                {p.label} · {p.currency} {p.amount} · {fmtDate(p.paid_at)}
              </Badge>
            ))}
          </div>
        )}

        {open ? <Messages consultId={thread.id} /> : (
          <p className="text-[11px] text-muted-foreground">Open to read the conversation.</p>
        )}
      </div>
    </div>
  );
};

/**
 * V68 — Diet Communication. Mounted ONLY when the tab is clicked (see UserDetailModal), and
 * every query below is paginated, so a learner with hundreds of consultations costs the same
 * to open as a learner with one.
 */
const DietCommunicationTab = ({ userId }) => {
  const [summary, setSummary] = useState(null);
  const [threads, setThreads] = useState([]);
  const [skip, setSkip] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [granting, setGranting] = useState(false);
  const [player, setPlayer] = useState(null);

  const loadPage = useCallback(async (nextSkip) => {
    setLoading(true);
    try {
      const res = await api.get(`/api/admin/diet-communication/${userId}/threads`, { params: { skip: nextSkip } });
      setThreads((prev) => (nextSkip ? [...prev, ...(res.data?.threads || [])] : (res.data?.threads || [])));
      setHasMore(!!res.data?.has_more);
      setSkip(nextSkip + (res.data?.threads || []).length);
    } catch (e) {
      setError(e?.response?.data?.detail?.message || 'Could not load the diet communication history');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await api.get(`/api/admin/diet-communication/${userId}/summary`);
        if (alive) setSummary(res.data);
      } catch (e) {
        if (alive) setError(e?.response?.data?.detail?.message || 'Could not load the summary');
      }
      if (alive) loadPage(0);
    })();
    return () => { alive = false; };
  }, [userId, loadPage]);

  const play = async (rec) => {
    try {
      const res = await api.get(`/api/admin/diet-communication/recording/${rec.id}/play`);
      setPlayer({ url: res.data?.embed_url, recordedAt: res.data?.recorded_at });
    } catch (e) {
      setError(e?.response?.data?.detail?.message || 'Could not open the recording');
    }
  };

  const c = summary?.counts || {};
  const entitled = !!summary?.diet_plan?.entitled;
  const addDietPlan = async (region) => {
    setGranting(true);
    try {
      const res = await api.post(`/api/admin/diet-communication/${userId}/entitlement`, { region });
      setSummary((s) => ({ ...(s || {}), diet_plan: res.data?.diet_plan }));
      setError(null);
    } catch (e) {
      setError(e?.response?.data?.detail?.message || 'Could not add the diet plan');
    } finally { setGranting(false); }
  };

  return (
    <div className="space-y-4" data-testid="diet-communication-tab">
      {/* V73 — until a diet plan sits on her account the dietician has no tools in the chat:
          no form, no documents. This is the unblock for anyone who paid before the feature. */}
      <div className={`rounded-xl border px-3 py-2.5 flex items-center gap-3 flex-wrap ${entitled ? 'border-emerald-200 bg-emerald-50/60' : 'border-amber-200 bg-amber-50/60'}`}
        data-testid="diet-entitlement-banner">
        <Salad className={`h-4 w-4 shrink-0 ${entitled ? 'text-emerald-600' : 'text-amber-600'}`} />
        <div className="flex-1 min-w-[220px]">
          <p className="text-sm font-medium">
            {entitled ? 'Diet plan is on her account' : 'No diet plan on her account'}
          </p>
          <p className="text-xs text-muted-foreground">
            {entitled
              ? `${summary.diet_plan.region === 'international' ? 'International' : 'India'}`
                + `${summary.diet_plan.complimentary ? ' · included with her plan' : ' · paid'}`
                + `${summary.diet_plan.source ? ` · added via ${String(summary.diet_plan.source).replace(/_/g, ' ')}` : ''}`
              : 'The dietician cannot send the assessment form or share documents until this is added.'}
          </p>
        </div>
        {!entitled && (
          <div className="flex gap-2">
            <Button size="sm" variant="outline" disabled={granting} onClick={() => addDietPlan('india')}
              data-testid="diet-grant-india">
              {granting ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : null}Add · India
            </Button>
            <Button size="sm" variant="outline" disabled={granting} onClick={() => addDietPlan('international')}
              data-testid="diet-grant-international">
              Add · International
            </Button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <Stat label="Chats" value={c.threads ?? '—'} />
        <Stat label="Video consults" value={c.calls ?? '—'} />
        <Stat label="Recordings" value={c.recordings ?? '—'} />
        <Stat label="Paid plans" value={c.paid_requests ?? '—'} />
      </div>

      {summary?.retention && (
        <p className="text-[11px] text-muted-foreground flex items-start gap-1.5" data-testid="diet-retention-note">
          <CalendarClock className="h-3.5 w-3.5 mt-0.5 shrink-0" />
          {summary.retention.policy}
          {summary.retention.edd ? ` Due date on file: ${fmtDate(summary.retention.edd)}.` : ''}
          {summary.can_play_recordings ? '' : ' Only a superadmin can watch a recording.'}
        </p>
      )}

      {error && (
        <p className="text-xs text-rose-600 flex items-center gap-1.5" data-testid="diet-comm-error">
          <AlertCircle className="h-3.5 w-3.5" />{error}
        </p>
      )}

      {loading && !threads.length ? (
        <div className="py-8 flex justify-center"><Loader2 className="h-5 w-5 animate-spin" /></div>
      ) : threads.length === 0 ? (
        <p className="text-sm text-muted-foreground py-6 text-center" data-testid="diet-comm-empty">
          No nutritionist conversations yet.
        </p>
      ) : (
        <div className="space-y-2">
          {threads.map((t) => (
            <Thread key={t.id} thread={t} canPlay={!!summary?.can_play_recordings} onPlay={play} />
          ))}
          {hasMore && (
            <Button variant="outline" size="sm" disabled={loading} onClick={() => loadPage(skip)}
                    data-testid="diet-comm-load-more">
              {loading ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}Load more conversations
            </Button>
          )}
        </div>
      )}

      <Dialog open={!!player} onOpenChange={(o) => !o && setPlayer(null)}>
        <DialogContent className="max-w-3xl" data-testid="recording-player-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg">
              Consultation recording · {fmtDateTime(player?.recordedAt)}
            </DialogTitle>
          </DialogHeader>
          {player?.url && (
            <div className="relative w-full" style={{ paddingTop: '56.25%' }}>
              <iframe
                src={player.url} title="Consultation recording" loading="lazy"
                className="absolute inset-0 w-full h-full rounded-lg border"
                allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture"
                allowFullScreen
                data-testid="recording-player-iframe"
              />
            </div>
          )}
          <p className="text-[11px] text-muted-foreground">
            Opened for audit. This view is logged against your account.
          </p>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DietCommunicationTab;
